import { ThemeColors } from '@/shared/types/theme';

/**
 * ============================================================================
 * VOLEY DESIGN SYSTEM - COLOR THEME MASTER FILE
 * ============================================================================
 * 
 * This is the SINGLE SOURCE OF TRUTH for all colors in the application.
 * 
 * HOW TO CHANGE BRAND COLORS:
 * 1. Update the values below (primary, secondary, etc.)
 * 2. Save this file
 * 3. Colors automatically update throughout the entire app
 * 
 * See DESIGN_SYSTEM.md for complete documentation.
 * ============================================================================
 */

export const lightTheme: ThemeColors = {
  // PRIMARY BRAND COLORS
  // These are your main brand colors. Change these to update your brand identity.
  
  primary: '#3581cf', // Main brand blue - buttons, links, highlights, primary CTAs
  primaryHover: '#2a6bb0', // Primary hover state (darker)
  secondary: '#3581cf', // Secondary accent - toggle switches, secondary CTAs
  
  // BACKGROUND COLORS
  
  background: '#F8FAFB', // Main page background
  foreground: '#374151', // Foreground elements
  sidebar: '#F8FAFB', // Sidebar background
  cardBackground: '#FFFFFF', // Cards and panels
  cardBackgroundHeader: '#F9FAFB', // Card headers
  
  // BORDER & STRUCTURE
  
  border: '#D5D9DF', // Borders for cards, inputs, dividers
  
  // TEXT COLORS
  
  textPrimary: '#374151', // Main text color (headings, body)
  textSecondary: '#4B5563', // Secondary/muted text (captions, labels)
  textTertiary: '#9CA3AF', // Tertiary/placeholder text (gray-400)
  darkGray: '#6B7280', // Tertiary text, subtle elements
  slateBlue: '#628298', // Accent text color
  
  // STATUS & SEMANTIC COLORS
  // These communicate state and meaning to users
  
  success: '#22C55E', // Success messages, checkmarks, positive actions
  error: '#ED4B4A', // Errors, destructive actions, warnings
  errorLight: '#FEE2E2', // Error backgrounds (red-100)
  warning: '#EDEC4A', // Warning states, caution messages
  warningLight: '#FEF3C7', // Warning backgrounds (amber-100)
  successLight: '#F0FDF4', // Success backgrounds (green-50)
  info: '#3581cf', // Informational messages, tips, help text
  
  // AVATAR COLOR PALETTE
  // Used for user and contact avatars throughout the app
  
  avatarColors: [
    '#E22E51', // Red
    '#E2652E', // Orange-red
    '#E2BF2E', // Gold
    '#99B431', // Yellow-green
    '#1E7145', // Green
    '#22577A', // Blue
    '#7E3877', // Purple
    '#E773BC', // Pink
    '#404040'  // Dark gray
  ],
  
  // TAG COLORS
  // Used for labels, categories, tags, and color coding
  
  tagColors: {
    red: '#CB2729',
    orange: '#EDAA4A',
    yellow: '#EDEC4A',
    green: '#31655C',
    blue: '#3581cf',
    pink: '#932E8F',
    slateBlue: '#628298',
    navy: '#1D435D',
    // Legacy aliases for backward compatibility
    purple: '#932E8F',
    teal: '#31655C',
    darkOrange: '#EDAA4A',
    magenta: '#932E8F',
    lime: '#31655C'
  },
  
  // UI COMPONENT COLORS
  // Interactive element states
  
  buttonHover: '#E8EAEE', // Button hover state background
  buttonActive: '#D5D9DF', // Button active/pressed state
  inputBackground: '#FFFFFF', // Input field backgrounds
  modalBackdrop: 'rgba(0, 0, 0, 0.5)', // Modal overlay
  hoverBackground: '#EFF0F2', // General hover backgrounds
  lightGray: '#F2F4F5', // Subtle backgrounds (received messages)
  selectedBackground: '#E6F4FF', // Selected item highlight
  messageBubble: '#28a4d9', // Sent message bubble color
  
  // STATUS INDICATORS
  // User presence and activity states
  
  online: '#22C55E', // User online indicator
  offline: '#9CA3AF', // User offline indicator
  pending: '#EDEC4A' // Pending/away status
};

/**
 * ============================================================================
 * DARK THEME
 * ============================================================================
 * 
 * Dark mode version of the theme. Follows the same structure as lightTheme.
 * Update these values to customize dark mode appearance.
 * ============================================================================
 */

export const darkTheme: ThemeColors = {
  // PRIMARY BRAND COLORS
  
  primary: '#4A9FE5', // Main brand blue (slightly brighter for dark mode)
  primaryHover: '#3B8AD4', // Primary hover state (slightly brighter)
  secondary: '#3581cf', // Secondary accent
  
  // BACKGROUND COLORS
  
  background: '#0F172A', // Main page background (slate-900)
  foreground: '#F8FAFC', // Foreground elements (slate-50)
  sidebar: '#0F172A', // Sidebar background
  cardBackground: '#1E293B', // Cards and panels (slate-800)
  cardBackgroundHeader: '#172033', // Card headers
  
  // BORDER & STRUCTURE
  
  border: '#334155', // Borders (slate-700)
  
  // TEXT COLORS
  
  textPrimary: '#F8FAFC', // Main text (slate-50)
  textSecondary: '#CBD5E1', // Secondary text (slate-300)
  textTertiary: '#64748B', // Tertiary/placeholder text (slate-500)
  darkGray: '#94A3B8', // Tertiary text (slate-400)
  slateBlue: '#628298', // Accent text
  
  // STATUS & SEMANTIC COLORS
  
  success: '#22C55E', // Success (same as light theme)
  error: '#F87171', // Error (red-400, brighter for visibility)
  errorLight: '#7F1D1D', // Error backgrounds (red-900 for dark mode)
  warning: '#FACC15', // Warning (yellow-400, brighter)
  warningLight: '#78350F', // Warning backgrounds (amber-900 for dark mode)
  successLight: '#14532D', // Success backgrounds (green-900 for dark mode)
  info: '#60A5FA', // Info (blue-400, brighter)

  // AVATAR COLOR PALETTE
  // Same as light theme for consistency
  
  avatarColors: [
    '#E22E51', '#E2652E', '#E2BF2E', '#99B431', 
    '#1E7145', '#22577A', '#7E3877', '#E773BC', '#404040'
  ],
  
  // TAG COLORS
  // Brighter variants for dark mode visibility
  
  tagColors: {
    red: '#F87171',
    orange: '#FB923C',
    yellow: '#FACC15',
    green: '#4ADE80',
    blue: '#60A5FA',
    pink: '#D946EF',
    slateBlue: '#93C5FD',
    navy: '#7DD3FC',
    // Legacy aliases
    purple: '#D946EF',
    teal: '#4ADE80',
    darkOrange: '#FB923C',
    magenta: '#D946EF',
    lime: '#4ADE80'
  },
  
  // UI COMPONENT COLORS
  
  buttonHover: '#273449', // Button hover
  buttonActive: '#334155', // Button active (slate-700)
  inputBackground: '#273449', // Input backgrounds
  modalBackdrop: 'rgba(0, 0, 0, 0.7)', // Modal overlay
  hoverBackground: '#273449', // General hover
  lightGray: '#273549', // Subtle backgrounds
  selectedBackground: '#1e3a5f', // Selected items (dark blue)
  messageBubble: '#305ac5', // Sent message bubbles
  
  // STATUS INDICATORS
  
  online: '#22C55E', // Online indicator
  offline: '#94A3B8', // Offline (slate-400)
  pending: '#FACC15' // Pending status
};

/**
 * ============================================================================
 * CSS VARIABLE MAPPING
 * ============================================================================
 * 
 * Maps CSS variable names to theme property paths.
 * This enables runtime theme switching via CSS variables.
 * 
 * DO NOT modify these unless adding new theme properties.
 * ============================================================================
 */
export const cssVariableMap = {
  '--color-primary': 'primary',
  '--color-primary-hover': 'primaryHover',
  '--color-secondary': 'secondary',
  '--color-background': 'background',
  '--color-foreground': 'foreground',
  '--color-sidebar': 'sidebar',
  '--color-card-background': 'cardBackground',
  '--color-card-background-header': 'cardBackgroundHeader',
  '--color-border': 'border',
  '--color-text-primary': 'textPrimary',
  '--color-text-secondary': 'textSecondary',
  '--color-text-tertiary': 'textTertiary',
  '--color-dark-gray': 'darkGray',
  '--color-slate-blue': 'slateBlue',
  
  // Status colors
  '--color-success': 'success',
  '--color-error': 'error',
  '--color-error-light': 'errorLight',
  '--color-warning': 'warning',
  '--color-warning-light': 'warningLight',
  '--color-success-light': 'successLight',
  '--color-info': 'info',
  
  // Avatar colors
  '--avatar-colors': 'avatarColors',
  '--avatar-color-1': 'avatarColors.0',
  '--avatar-color-2': 'avatarColors.1',
  '--avatar-color-3': 'avatarColors.2',
  '--avatar-color-4': 'avatarColors.3',
  '--avatar-color-5': 'avatarColors.4',
  '--avatar-color-6': 'avatarColors.5',
  '--avatar-color-7': 'avatarColors.6',
  '--avatar-color-8': 'avatarColors.7',
  '--avatar-color-9': 'avatarColors.8',
  
  // UI component
  '--color-button-hover': 'buttonHover',
  '--color-button-active': 'buttonActive',
  '--color-input-background': 'inputBackground',
  '--color-modal-backdrop': 'modalBackdrop',
  '--color-hover-background': 'hoverBackground',
  '--color-selected-background': 'selectedBackground',
  '--color-message-bubble': 'messageBubble',
  
  // Status indicators
  '--color-online': 'online',
  '--color-offline': 'offline',
  '--color-pending': 'pending',
  
  // Accent colors
  '--accent-blue': 'primary',    // #4AACED
  '--accent-red': 'error',       // #ED4B4A
  '--accent-green': 'success',   // #31655C (updated)
  '--accent-yellow': 'warning',  // #EDEC4A
  '--accent-orange': 'tagColors.orange', // #EDAA4A
  '--accent-pink': 'tagColors.pink',     // #932E8F (updated)
  '--accent-slate-blue': 'slateBlue',    // #628298
  '--accent-navy': 'tagColors.navy',     // #1D435D (new)
}; 